import { API_BASE_URL } from '../auth/apiClient';
import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    Box,
    Flex,
    IconButton,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    Text,
    VStack,
    HStack,
    Badge,
    useBreakpointValue,
    ButtonGroup,
    Grid,
    Image,
    Spinner,
    Center,
} from "@chakra-ui/react";
import {
    MdOutlineKeyboardArrowLeft,
    MdOutlineKeyboardArrowRight,
    MdMyLocation,
    MdOutlineLegendToggle,
    MdClose,
} from "react-icons/md";
import * as maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";

import { WaterLevelData, WaterLevelFilterState } from "../types/waterLevel";
import {
    hexToColorScheme,
    getLabelByDashboardType,
    getColorByDashboardType,
    formatDateTime,
    WATER_LEVEL_COLORS,
    WATER_LEVEL_KEMARAU_COLORS,
    RAINFALL_COLORS
} from "../utils/waterLevel";
import { getCategoryColor } from "../utils/CategoryColors";
import { useWaterLevel } from "../hooks/useWaterLevel";
import { FiMinus, FiPlus } from "react-icons/fi";
import FilterWaterLevel from "../components/FilterWaterLevel";
import * as turf from '@turf/turf';
import { TbLine } from "react-icons/tb";

// Helper function for badge color scheme
const getBadgeColorScheme = (
    dashboardType: 'water_level' | 'curah_hujan' | 'water_level_dermaga',
    quantity: number,
    season: 'normal' | 'kemarau' = 'normal'
): string => {
    const hexColor = getColorByDashboardType(dashboardType, quantity, season);
    return hexToColorScheme(hexColor);
};

// Legend Item Component
const LegendItem: React.FC<{ color: string; description: string }> = ({ color, description }) => (
    <HStack spacing={3} align="center">
        <Box w="16px" h="16px" bg={color} borderRadius="full" border="1px solid" borderColor="gray.300" />
        <Text fontSize="sm" color="gray.700">
            {description}
        </Text>
    </HStack>
);

const MapWaterLevelComponent = () => {
    const [selectedBlock, setSelectedBlock] = useState<{
        LandUse_26?: string;
        blok_code?: string;
        waterLevelData?: WaterLevelData[];
        isLKK?: boolean;
    } | null>(null);
    const [selectedItem, setSelectedItem] = useState<WaterLevelData | null>(null);
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const mapContainerRef = useRef<HTMLDivElement>(null);
    const [map, setMap] = useState<maplibregl.Map | null>(null);
    const [pendingApply, setPendingApply] = useState(false);
    const [isSatellite, setIsSatellite] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [isLegendOpen, setIsLegendOpen] = useState(false);
    const [showConnectionLines, setShowConnectionLines] = useState(true);
    const [waterLevelData, setWaterLevelData] = useState<WaterLevelData[]>([]);
    const waterLevelDataRef = useRef<WaterLevelData[]>([]);

    const {
        filters,
        fetchWaterLevelData,
        updateFilters,
        clearFilters: clearWaterLevelFilters,
    } = useWaterLevel();

    const defaultSidebarState = useBreakpointValue({ base: true, md: true });

    // Helper function to determine dashboard type based on category_id
    const getDashboardTypeFromCategory = (categoryId?: number): 'water_level' | 'curah_hujan' | 'water_level_dermaga' => {
        if (categoryId) {
            switch (categoryId) {
                case 15: return 'curah_hujan'; // Curah Hujan
                case 14: return 'water_level_dermaga'; // Water Level Dermaga
                case 12: // Water Level Block
                case 13: // Water Level Parit
                default: return 'water_level';
            }
        }
        return filters.dashboard_type || 'water_level';
    };

    useEffect(() => {
        setSidebarOpen(defaultSidebarState ?? true);
    }, [defaultSidebarState]);

    useEffect(() => {
        waterLevelDataRef.current = waterLevelData;
    }, [waterLevelData]);

    const getBlockColor = (
        blockData: WaterLevelData[],
        dashboardType: 'water_level' | 'curah_hujan' | 'water_level_dermaga',
        season: 'normal' | 'kemarau' = 'normal'
    ): string => {
        if (!blockData || blockData.length === 0) {
            return '#d9ecbd';
        }
        const latestInspection = blockData[0];
        const quantity = latestInspection.water_level_qty;
        return getColorByDashboardType(dashboardType, quantity, season);
    };

    const updateBlockColors = useCallback(async () => {
        if (!map) return;

        const dashboardType = getDashboardTypeFromCategory(filters.category_id);
        const season = filters.season || 'normal';

        // Update GBS blocks
        const geoSource = map.getSource("geojson-source") as maplibregl.GeoJSONSource;
        if (geoSource) {
            try {
                const geoData: any = await geoSource.getData();
                geoData.features.forEach((feature: any) => {
                    const blockName = feature.properties?.LandUse_26;
                    const blockData = waterLevelDataRef.current.filter(item => item.blok === blockName);
                    // Reset color to default white if no data
                    feature.properties.color = blockData.length > 0 ? getBlockColor(blockData, dashboardType, season) : '#d9ecbd';
                });
                geoSource.setData(geoData);

                map.setPaintProperty('geojson-layer', 'fill-color', [
                    'case',
                    ['has', 'color'],
                    ['get', 'color'],
                    '#d9ecbd'
                ]);
            } catch (error) {
                console.error('Error updating GBS block colors:', error);
            }
        }

        // Update LKK blocks  
        const lkkSource = map.getSource("lkk-source") as maplibregl.GeoJSONSource;
        if (lkkSource) {
            try {
                const lkkData: any = await lkkSource.getData();
                lkkData.features.forEach((feature: any) => {
                    const rawCode = feature.properties?.blok_code;
                    // Potong 2 digit terakhir jika panjang kode 8 karakter
                    const blockCode6 = rawCode && rawCode.length === 8 ? rawCode.slice(0, 6) : rawCode;
                    const blockData = waterLevelDataRef.current.filter(item => item.blok === blockCode6);
                    // Reset color to default white if no data
                    feature.properties.color = blockData.length > 0 ? getBlockColor(blockData, dashboardType, season) : '#d9ecbd';
                });
                lkkSource.setData(lkkData);

                map.setPaintProperty('lkk-layer', 'fill-color', [
                    'case',
                    ['has', 'color'],
                    ['get', 'color'],
                    '#d9ecbd'
                ]);
            } catch (error) {
                console.error('Error updating LKK block colors:', error);
            }
        }
    }, [map, filters.dashboard_type, filters.category_id, filters.season]);

    useEffect(() => {
        // Update block colors when data changes
        if (map && waterLevelData.length > 0) {
            updateBlockColors();
        }
    }, [waterLevelData, map, updateBlockColors]);

    const handleFilterChange = useCallback(
        (newFilters: WaterLevelFilterState) => {
            updateFilters(newFilters);
        },
        [updateFilters]
    );

    const clearFilters = useCallback(() => {
        clearWaterLevelFilters();
        setPendingApply(true);
    }, [clearWaterLevelFilters]);

    const handleApplyFilters = useCallback(() => {
        setPendingApply(true);
    }, []);

    useEffect(() => {
        if (!mapContainerRef.current) return;

        const mapInstance = new maplibregl.Map({
            container: mapContainerRef.current,
            style: "https://api.maptiler.com/maps/streets-v2/style.json?key=Q3fXi2sghTMg1wQSFgxK",
            center: [104.083064, -3.125992],
            zoom: 10.9,
            attributionControl: false,
        });

        mapInstance.on("load", async () => {
            mapInstance.addSource("satellite", {
                type: "raster",
                tiles: [
                    "https://api.maptiler.com/tiles/satellite-v2/{z}/{x}/{y}.jpg?key=Q3fXi2sghTMg1wQSFgxK",
                ],
                tileSize: 256,
            });
            mapInstance.addLayer({
                id: "satellite-layer",
                type: "raster",
                source: "satellite",
                layout: { visibility: "none" },
            });

            // Then add GeoJSON and water level layers (so they appear on top)
            await loadGeoJSON(mapInstance);
            await fetchWaterLevelMapData(mapInstance);

            // Ensure GeoJSON and water level layers are above satellite by default
            if (mapInstance.getLayer("geojson-layer")) {
                mapInstance.moveLayer("geojson-layer");
            }
            if (mapInstance.getLayer("geojson-label")) {
                mapInstance.moveLayer("geojson-label");
            }
            if (mapInstance.getLayer("lkk-layer")) {
                mapInstance.moveLayer("lkk-layer");
            }
            if (mapInstance.getLayer("lkk-label")) {
                mapInstance.moveLayer("lkk-label");
            }
            if (mapInstance.getLayer("mrn-layer")) {
                mapInstance.moveLayer("mrn-layer");
            }
            if (mapInstance.getLayer("mrn-label")) {
                mapInstance.moveLayer("mrn-label");
            }
            if (mapInstance.getLayer("waterlevel-marker")) {
                mapInstance.moveLayer("waterlevel-marker");
            }
        });

        setMap(mapInstance);

        return () => {
            if (mapInstance) {
                mapInstance.remove();
            }
        };
    }, []);

    useEffect(() => {
        if (!map) return;
        if (!pendingApply) return;

        fetchWaterLevelMapData(map, filters)
            .catch(console.error)
            .finally(() => {
                setPendingApply(false);
            });
    }, [map, pendingApply, filters]);

    // Separate effect for connection lines visibility
    useEffect(() => {
        if (!map) return;

        console.log("Toggling connection lines visibility:", showConnectionLines);

        // Fungsi untuk mengubah visibilitas
        const updateVisibility = () => {
            try {
                if (map.getLayer('connection-lines')) {
                    const visibility = showConnectionLines ? 'visible' : 'none';
                    map.setLayoutProperty('connection-lines', 'visibility', visibility);
                    console.log("Connection lines visibility set to:", visibility);
                    return true;
                }
                return false;
            } catch (err) {
                console.error("Error setting connection line visibility:", err);
                return false;
            }
        };

        // Coba update jika map sudah siap
        if (map.isStyleLoaded()) {
            if (!updateVisibility() && showConnectionLines) {
                console.log("Connection lines layer not found, will retry later");
                // Layer belum ada, tunggu 1 detik dan coba lagi
                setTimeout(updateVisibility, 1000);
            }
        } else {
            // Tunggu map load
            map.once('styledata', updateVisibility);
        }
    }, [map, showConnectionLines]);

    const loadGeoJSON = async (mapInstance: maplibregl.Map) => {
        try {
            // Load batas blok kebun GBS
            const response = await fetch("/data/batas_blok_kebun_gbs.geojson");
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const geojsonData = await response.json();

            if (!mapInstance.getSource("geojson-source")) {
                mapInstance.addSource("geojson-source", {
                    type: "geojson",
                    data: geojsonData,
                });
                mapInstance.addLayer({
                    id: "geojson-layer",
                    type: "fill",
                    source: "geojson-source",
                    paint: {
                        "fill-color": "#ffffff",
                        "fill-opacity": 0.5,
                        "fill-outline-color": "#000000",
                    },
                });
                mapInstance.addLayer({
                    id: "geojson-label",
                    type: "symbol",
                    source: "geojson-source",
                    layout: {
                        "text-field": ["get", "LandUse_26"],
                        "text-size": 14,
                        "text-anchor": "center",
                        "text-offset": [0, 0],
                    },
                    paint: {
                        "text-color": "#000000",
                        "text-halo-width": 2,
                    },
                });

                // Add click event for GBS blocks
                mapInstance.on('click', 'geojson-layer', (e) => {
                    if (e.features && e.features.length > 0) {
                        const feature = e.features[0];
                        const blockName = feature.properties?.LandUse_26;

                        // Find water level data for this block
                        const blockData = waterLevelDataRef.current.filter(item =>
                            item.blok === blockName
                        );

                        // Create block object using only API data
                        setSelectedBlock({
                            LandUse_26: blockName,
                            isLKK: false,
                            waterLevelData: blockData
                        });
                    }
                });

                mapInstance.on('mouseenter', 'geojson-layer', () => {
                    mapInstance.getCanvas().style.cursor = 'pointer';
                });

                mapInstance.on('mouseleave', 'geojson-layer', () => {
                    mapInstance.getCanvas().style.cursor = '';
                });
            }

            // Load Blok Planted LKK
            const lkkResponse = await fetch("/data/Blok_Planted_LKK.geojson");
            if (!lkkResponse.ok) {
                throw new Error(`HTTP error! Status: ${lkkResponse.status}`);
            }

            const lkkData = await lkkResponse.json();

            // Set warna default untuk LKK blocks
            lkkData.features.forEach((feature: any) => {
                feature.properties.color = '#ffffff';
            });

            if (!mapInstance.getSource("lkk-source")) {
                mapInstance.addSource("lkk-source", {
                    type: "geojson",
                    data: lkkData
                });
                mapInstance.addLayer({
                    id: "lkk-layer",
                    type: "fill",
                    source: "lkk-source",
                    paint: {
                        "fill-color": "#ffffff",
                        "fill-opacity": 0.5,
                        "fill-outline-color": "#000000",
                    },
                });
                mapInstance.addLayer({
                    id: "lkk-label",
                    type: "symbol",
                    source: "lkk-source",
                    layout: {
                        "text-field": ["get", "blok_code"],
                        "text-size": 14,
                        "text-anchor": "center",
                        "text-offset": [0, 0],
                    },
                    paint: {
                        "text-color": "#000000",
                        "text-halo-width": 2,
                    },
                });

                // Add click event for LKK blocks
                mapInstance.on('click', 'lkk-layer', (e) => {
                    if (e.features && e.features.length > 0) {
                        const feature = e.features[0];
                        const rawCode = feature.properties?.blok_code;
                        // Potong 2 digit terakhir jika panjang kode 8 karakter
                        const blockCode6 = rawCode && rawCode.length === 8 ? rawCode.slice(0, 6) : rawCode;

                        // Find water level data for this block menggunakan kode 6 digit
                        const blockData = waterLevelDataRef.current.filter(item =>
                            item.blok === blockCode6
                        );

                        // Create block object using only API data
                        setSelectedBlock({
                            blok_code: blockCode6, // Simpan kode 6 digit untuk display
                            isLKK: true,
                            waterLevelData: blockData
                        });
                    }
                });

                mapInstance.on('mouseenter', 'lkk-layer', () => {
                    mapInstance.getCanvas().style.cursor = 'pointer';
                });

                mapInstance.on('mouseleave', 'lkk-layer', () => {
                    mapInstance.getCanvas().style.cursor = '';
                });
            } else {
                (mapInstance.getSource("lkk-source") as maplibregl.GeoJSONSource).setData(lkkData);
            }

            // Load Batas MRN
            const mrnResponse = await fetch("/data/mrn_map.geojson");
            if (mrnResponse.ok) {
                const mrnData = await mrnResponse.json();
                mrnData.features.forEach((feature: any) => {
                    feature.properties.color = '#ffffff';
                });

                if (!mapInstance.getSource("mrn-source")) {
                    mapInstance.addSource("mrn-source", {
                        type: "geojson",
                        data: mrnData
                    });
                    mapInstance.addLayer({
                        id: "mrn-layer",
                        type: "fill",
                        source: "mrn-source",
                        paint: {
                            "fill-color": "#ffffff",
                            "fill-opacity": 0.5,
                            "fill-outline-color": "#000000",
                        },
                    });
                    mapInstance.addLayer({
                        id: "mrn-label",
                        type: "symbol",
                        source: "mrn-source",
                        layout: {
                            "text-field": ["coalesce", ["get", "blok_code"], ["get", "LandUse_26"], ""],
                            "text-size": 14,
                            "text-anchor": "center",
                            "text-offset": [0, 0],
                        },
                        paint: {
                            "text-color": "#000000",
                            "text-halo-width": 2,
                        },
                    });

                    // Add click event for MRN blocks
                    mapInstance.on('click', 'mrn-layer', (e) => {
                        if (e.features && e.features.length > 0) {
                            const feature = e.features[0];
                            const rawCode = feature.properties?.blok_code ?? feature.properties?.LandUse_26;
                            const blockCode6 = rawCode && rawCode.length === 8 ? rawCode.slice(0, 6) : rawCode;

                            const blockData = waterLevelDataRef.current.filter(item =>
                                item.blok === blockCode6
                            );

                            setSelectedBlock({
                                blok_code: blockCode6,
                                isLKK: false,
                                waterLevelData: blockData
                            });
                        }
                    });

                    mapInstance.on('mouseenter', 'mrn-layer', () => {
                        mapInstance.getCanvas().style.cursor = 'pointer';
                    });

                    mapInstance.on('mouseleave', 'mrn-layer', () => {
                        mapInstance.getCanvas().style.cursor = '';
                    });
                } else {
                    (mapInstance.getSource("mrn-source") as maplibregl.GeoJSONSource).setData(mrnData);
                }
            }
        } catch (error) {
            console.error("Error loading GeoJSON file:", error);
        }
    };

    const fetchWaterLevelMapData = async (
        mapInstance: maplibregl.Map,
        filterParams?: WaterLevelFilterState
    ) => {
        try {
            // Remove existing water level layers
            if (mapInstance.getLayer("waterlevel-marker")) {
                mapInstance.removeLayer("waterlevel-marker");
            }
            if (mapInstance.getLayer("wms-status-marker")) {
                mapInstance.removeLayer("wms-status-marker");
            }
            if (mapInstance.getSource("waterlevel-source")) {
                mapInstance.removeSource("waterlevel-source");
            }
            if (mapInstance.getSource("wms-status-source")) {
                mapInstance.removeSource("wms-status-source");
            }

            // Remove connection lines when switching modes
            if (mapInstance.getLayer("connection-lines")) {
                mapInstance.removeLayer("connection-lines");
            }
            if (mapInstance.getSource("connection-lines-source")) {
                mapInstance.removeSource("connection-lines-source");
            }

            const filtersToUse = filterParams || filters;
            console.log("Fetching water level data with params:", filtersToUse);

            // Use the hook's fetchWaterLevelData method
            const waterLevelDataResponse = await fetchWaterLevelData(filtersToUse);
            setWaterLevelData(waterLevelDataResponse);

            // Reset all block colors to default before applying new colors
            const dashboardType = getDashboardTypeFromCategory(filtersToUse.category_id);

            // Reset GBS blocks to default color
            const geoSource = mapInstance.getSource("geojson-source") as maplibregl.GeoJSONSource;
            if (geoSource) {
                try {
                    const geoData: any = await geoSource.getData();
                    geoData.features.forEach((feature: any) => {
                        feature.properties.color = '#d9ecbd'; // Default color
                    });
                    geoSource.setData(geoData);
                } catch (error) {
                    console.error('Error resetting GBS block colors:', error);
                }
            }

            // Reset LKK blocks to default color
            const lkkSource = mapInstance.getSource("lkk-source") as maplibregl.GeoJSONSource;
            if (lkkSource) {
                try {
                    const lkkData: any = await lkkSource.getData();
                    lkkData.features.forEach((feature: any) => {
                        feature.properties.color = '#d9ecbd'; // Default color
                    });
                    lkkSource.setData(lkkData);
                } catch (error) {
                    console.error('Error resetting LKK block colors:', error);
                }
            }

            // Only apply colors if there is data
            if (waterLevelDataResponse.length > 0) {
                // Update block colors with new data
                if (geoSource) {
                    try {
                        const geoData: any = await geoSource.getData();
                        geoData.features.forEach((feature: any) => {
                            const blockName = feature.properties?.LandUse_26;
                            const blockData = waterLevelDataResponse.filter(item => item.blok === blockName);
                            feature.properties.color = blockData.length > 0 ? getBlockColor(blockData, dashboardType, filtersToUse.season) : '#d9ecbd';
                        });
                        geoSource.setData(geoData);
                    } catch (error) {
                        console.error('Error updating GBS block colors:', error);
                    }
                }

                if (lkkSource) {
                    try {
                        const lkkData: any = await lkkSource.getData();
                        lkkData.features.forEach((feature: any) => {
                            const rawCode = feature.properties?.blok_code;
                            // Potong 2 digit terakhir jika panjang kode 8 karakter
                            const blockCode6 = rawCode && rawCode.length === 8 ? rawCode.slice(0, 6) : rawCode;
                            const blockData = waterLevelDataResponse.filter(item => item.blok === blockCode6);
                            feature.properties.color = blockData.length > 0 ? getBlockColor(blockData, dashboardType, filtersToUse.season) : '#d9ecbd';
                        });
                        lkkSource.setData(lkkData);
                    } catch (error) {
                        console.error('Error updating LKK block colors:', error);
                    }
                }
            }

            // Show inspection points if the option is enabled
            if (filtersToUse.show_inspection_points) {
                const markerGeoJSON: GeoJSON.FeatureCollection<GeoJSON.Point, any> = {
                    type: "FeatureCollection",
                    features: waterLevelDataResponse.map((item) => ({
                        type: "Feature",
                        geometry: {
                            type: "Point",
                            coordinates: [item.longitude, item.latitude],
                        },
                        properties: {
                            ...item,
                            marker_color: getCategoryColor(item.category),
                        },
                    })),
                };

                mapInstance.addSource("waterlevel-source", {
                    type: "geojson",
                    data: markerGeoJSON,
                });

                mapInstance.addLayer({
                    id: "waterlevel-marker",
                    type: "circle",
                    source: "waterlevel-source",
                    paint: {
                        "circle-radius": 6,
                        "circle-color": ["get", "marker_color"],
                        "circle-opacity": 0.7,
                        "circle-stroke-width": 1,
                        "circle-stroke-color": "#374151",
                    },
                });

                // Create connection lines from inspection points to blocks
                const lineFeatures: GeoJSON.Feature<GeoJSON.LineString>[] = [];

                // Get geojson data for blocks
                const geojsonSource = mapInstance.getSource('geojson-source') as maplibregl.GeoJSONSource;
                const lkkSource = mapInstance.getSource('lkk-source') as maplibregl.GeoJSONSource;

                // Get the actual GeoJSON data
                const geojsonData = geojsonSource?._data as GeoJSON.FeatureCollection;
                const lkkData = lkkSource?._data as GeoJSON.FeatureCollection;

                if (geojsonData || lkkData) {
                    waterLevelDataResponse.forEach((item) => {
                        // Skip if we don't have valid longitude/latitude
                        if (!item.longitude || !item.latitude || isNaN(item.longitude) || isNaN(item.latitude)) {
                            console.warn("Skipping inspection with invalid coordinates:", item.id || "unknown");
                            return;
                        }

                        let blockFeature: any = null;

                        // First try to find in GBS blocks
                        if (geojsonData) {
                            blockFeature = geojsonData.features.find((f: any) =>
                                f.properties?.LandUse_26?.toLowerCase() === item.blok?.toLowerCase()
                            );
                        }

                        // If not found, try LKK blocks
                        if (!blockFeature && lkkData) {
                            blockFeature = lkkData.features.find((f: any) => {
                                const rawCode = f.properties?.blok_code;
                                // Potong 2 digit terakhir jika panjang kode 8 karakter
                                const blockCode6 = rawCode && rawCode.length === 8 ? rawCode.slice(0, 6) : rawCode;
                                return blockCode6 && item.blok && blockCode6.toLowerCase() === item.blok.toLowerCase();
                            });
                        }

                        if (blockFeature) {
                            try {
                                // Calculate block center
                                const centerMass = turf.centerOfMass(blockFeature);

                                // Validate center coordinates
                                if (!centerMass?.geometry?.coordinates ||
                                    !Array.isArray(centerMass.geometry.coordinates) ||
                                    centerMass.geometry.coordinates.length !== 2 ||
                                    isNaN(centerMass.geometry.coordinates[0]) ||
                                    isNaN(centerMass.geometry.coordinates[1])) {
                                    console.warn(`Invalid block center for ${item.blok}, using fallback calculation`);

                                    // Fallback to simpler calculation
                                    const coords = (blockFeature.geometry as any)?.coordinates?.[0] || [];
                                    if (coords.length > 0) {
                                        let sumLng = 0, sumLat = 0;
                                        coords.forEach((coord: any) => {
                                            sumLng += coord[0];
                                            sumLat += coord[1];
                                        });
                                        const blockCenter = [sumLng / coords.length, sumLat / coords.length];

                                        const point = turf.point([item.longitude, item.latitude]);
                                        const isInside = turf.booleanPointInPolygon(point, blockFeature as any);

                                        if (!isInside) {
                                            lineFeatures.push({
                                                type: 'Feature',
                                                geometry: {
                                                    type: 'LineString',
                                                    coordinates: [
                                                        [item.longitude, item.latitude],
                                                        blockCenter
                                                    ]
                                                },
                                                properties: {
                                                    category_color: getCategoryColor(item.category),
                                                    inspection_id: item.id,
                                                    blok: item.blok
                                                }
                                            });
                                        }
                                    }
                                } else {
                                    // Normal path - use centerOfMass result
                                    const blockCenter = centerMass.geometry.coordinates;
                                    const point = turf.point([item.longitude, item.latitude]);
                                    const isInside = turf.booleanPointInPolygon(point, blockFeature as any);

                                    if (!isInside) {
                                        lineFeatures.push({
                                            type: 'Feature',
                                            geometry: {
                                                type: 'LineString',
                                                coordinates: [
                                                    [item.longitude, item.latitude],
                                                    blockCenter
                                                ]
                                            },
                                            properties: {
                                                category_color: getCategoryColor(item.category),
                                                inspection_id: item.id,
                                                blok: item.blok
                                            }
                                        });
                                    }
                                }
                            } catch (err) {
                                console.error(`Error processing line for inspection ${item.id || "unknown"}:`, err);
                            }
                        }
                    });
                }

                console.log(`Validated ${lineFeatures.length} line features, adding to map...`);

                const connectionLinesGeoJSON: GeoJSON.FeatureCollection<GeoJSON.LineString> = {
                    type: "FeatureCollection",
                    features: lineFeatures
                };

                mapInstance.addSource("connection-lines-source", {
                    type: "geojson",
                    data: connectionLinesGeoJSON,
                });

                mapInstance.addLayer({
                    id: "connection-lines",
                    type: "line",
                    source: "connection-lines-source",
                    layout: { visibility: showConnectionLines ? "visible" : "none" },
                    paint: {
                        "line-color": ["get", "category_color"],
                        "line-width": 2,
                        "line-opacity": 0.7
                    },
                });

                // REMOVED: Click event handlers for inspection points
                // We don't want these to be clickable anymore
            }

            // Show WMS status points if any status is selected
            if (filtersToUse.wms_status_array && filtersToUse.wms_status_array.length > 0) {
                // Get geojson data for blocks
                const geojsonSource = mapInstance.getSource('geojson-source') as maplibregl.GeoJSONSource;
                const lkkSource = mapInstance.getSource('lkk-source') as maplibregl.GeoJSONSource;

                // Get the actual GeoJSON data
                const geojsonData = geojsonSource?._data as GeoJSON.FeatureCollection;
                const lkkData = lkkSource?._data as GeoJSON.FeatureCollection;

                // Filter data based on selected WMS status - FIX: Handle null wms_status
                const filteredWmsData = waterLevelDataResponse.filter(item => {
                    // Skip items with null or undefined wms_status
                    if (!item.wms_status) {
                        return false;
                    }
                    return filtersToUse.wms_status_array!.includes(item.wms_status.toLowerCase());
                });

                console.log("Filtered WMS data:", filteredWmsData);
                console.log("Selected WMS statuses:", filtersToUse.wms_status_array);
                console.log("Total water level data:", waterLevelDataResponse.length);

                if (filteredWmsData.length > 0 && (geojsonData || lkkData)) {
                    const wmsMarkerFeatures: GeoJSON.Feature<GeoJSON.Point, any>[] = [];

                    // Group data by block to show one point per block
                    const blockStatusMap = new Map<string, WaterLevelData[]>();
                    filteredWmsData.forEach(item => {
                        const blockKey = item.blok?.toLowerCase() || '';
                        if (blockKey) { // Only process items with valid block names
                            if (!blockStatusMap.has(blockKey)) {
                                blockStatusMap.set(blockKey, []);
                            }
                            blockStatusMap.get(blockKey)!.push(item);
                        }
                    });

                    console.log("Block status map:", blockStatusMap);

                    // Create markers at block centers
                    blockStatusMap.forEach((items, blockKey) => {
                        if (!blockKey) return;

                        let blockFeature: any = null;

                        // First try to find in GBS blocks
                        if (geojsonData) {
                            blockFeature = geojsonData.features.find((f: any) =>
                                f.properties?.LandUse_26?.toLowerCase() === blockKey
                            );
                        }

                        // If not found, try LKK blocks
                        if (!blockFeature && lkkData) {
                            blockFeature = lkkData.features.find((f: any) => {
                                const rawCode = f.properties?.blok_code;
                                // Potong 2 digit terakhir jika panjang kode 8 karakter
                                const blockCode6 = rawCode && rawCode.length === 8 ? rawCode.slice(0, 6) : rawCode;
                                return blockCode6 && blockCode6.toLowerCase() === blockKey;
                            });
                        }

                        console.log(`Block ${blockKey} found:`, !!blockFeature);

                        if (blockFeature) {
                            try {
                                // Calculate block center
                                const centerMass = turf.centerOfMass(blockFeature);
                                let blockCenter: number[] = [];

                                if (!centerMass?.geometry?.coordinates ||
                                    !Array.isArray(centerMass.geometry.coordinates) ||
                                    centerMass.geometry.coordinates.length !== 2 ||
                                    isNaN(centerMass.geometry.coordinates[0]) ||
                                    isNaN(centerMass.geometry.coordinates[1])) {

                                    // Fallback to simpler calculation
                                    const coords = (blockFeature.geometry as any)?.coordinates?.[0] || [];
                                    if (coords.length > 0) {
                                        let sumLng = 0, sumLat = 0;
                                        coords.forEach((coord: any) => {
                                            sumLng += coord[0];
                                            sumLat += coord[1];
                                        });
                                        blockCenter = [sumLng / coords.length, sumLat / coords.length];
                                    }
                                } else {
                                    blockCenter = centerMass.geometry.coordinates;
                                }

                                if (blockCenter.length === 2) {
                                    // Use the most recent item for this block
                                    const latestItem = items[0];

                                    // Determine color based on WMS status - FIX: Handle null wms_status
                                    let statusColor = "#808080"; // Default gray
                                    if (latestItem.wms_status) {
                                        const status = latestItem.wms_status.toLowerCase();
                                        if (status === 'k') {
                                            statusColor = "#DD6B20"; // Orange for Kotor
                                        } else if (status === 'r') {
                                            statusColor = "#E53E3E"; // Red for Rusak
                                        }
                                    }

                                    wmsMarkerFeatures.push({
                                        type: "Feature",
                                        geometry: {
                                            type: "Point",
                                            coordinates: blockCenter,
                                        },
                                        properties: {
                                            wms_color: statusColor,
                                            block_name: blockKey,
                                            total_items: items.length,
                                            // Only include essential data for display purposes
                                            status: latestItem.wms_status,
                                        },
                                    });

                                    console.log(`Added WMS marker for block ${blockKey} with color ${statusColor}`);
                                }
                            } catch (err) {
                                console.error(`Error processing WMS marker for block ${blockKey}:`, err);
                            }
                        }
                    });

                    console.log("Total WMS marker features created:", wmsMarkerFeatures.length);

                    if (wmsMarkerFeatures.length > 0) {
                        const wmsMarkerGeoJSON: GeoJSON.FeatureCollection<GeoJSON.Point, any> = {
                            type: "FeatureCollection",
                            features: wmsMarkerFeatures,
                        };

                        mapInstance.addSource("wms-status-source", {
                            type: "geojson",
                            data: wmsMarkerGeoJSON,
                        });

                        mapInstance.addLayer({
                            id: "wms-status-marker",
                            type: "circle",
                            source: "wms-status-source",
                            paint: {
                                "circle-radius": 6,
                                "circle-color": ["get", "wms_color"],
                                "circle-opacity": 0.7,
                                "circle-stroke-width": 1,
                                "circle-stroke-color": "#FFFFFF",
                            },
                        });

                        console.log("WMS status markers added to map successfully");

                    } else {
                        console.log("No WMS marker features to add to map");
                    }
                } else {
                    console.log("No filtered WMS data or no geojson data available");
                    console.log("Filtered WMS data length:", filteredWmsData.length);
                    console.log("Has geojson data:", !!geojsonData);
                    console.log("Has lkk data:", !!lkkData);
                }
            }
        } catch (error) {
            console.error("Error fetching water level data:", error);
        }
    };

    const focusToArea = () => {
        if (map) {
            map.flyTo({
                center: [104.083064, -3.125992],
                zoom: 10.9,
                essential: true,
            });
        }
    };

    const zoomIn = () => {
        if (map) map.zoomIn();
    };

    const zoomOut = () => {
        if (map) map.zoomOut();
    };

    const toggleSatellite = () => {
        if (!map) return;
        const currentVisibility = map.getLayoutProperty(
            "satellite-layer",
            "visibility"
        );
        if (currentVisibility === "visible") {
            map.setLayoutProperty("satellite-layer", "visibility", "none");
            setIsSatellite(false);
        } else {
            map.setLayoutProperty("satellite-layer", "visibility", "visible");
            setIsSatellite(true);

            // Ensure GeoJSON and water level layers are above satellite
            if (map.getLayer("geojson-layer")) {
                map.moveLayer("geojson-layer");
            }
            if (map.getLayer("geojson-label")) {
                map.moveLayer("geojson-label");
            }
            if (map.getLayer("lkk-layer")) {
                map.moveLayer("lkk-layer");
            }
            if (map.getLayer("lkk-label")) {
                map.moveLayer("lkk-label");
            }
            if (map.getLayer("waterlevel-marker")) {
                map.moveLayer("waterlevel-marker");
            }
        }
    };

    const getDashboardTitle = () => {
        if (filters.category_id) {
            switch (filters.category_id) {
                case 12: return 'Water Level Block';
                case 13: return 'Water Level Parit';
                case 14: return 'Water Level Dermaga';
                case 15: return 'Curah Hujan';
                default: return 'Water Level';
            }
        }
        return filters.dashboard_type === 'curah_hujan' ? 'Curah Hujan' : 'Water Level';
    };

    const dashboardTitle = getDashboardTitle();

    const DetailItem = ({ label, value }: { label: string; value?: string | React.ReactNode }) => (
        <Box>
            <Text fontSize="sm" color="gray.500" mb={0}>
                {label}
            </Text>
            {typeof value === 'string' ? (
                <Text fontWeight="500">{value || "-"}</Text>
            ) : (
                value || <Text fontWeight="500">-</Text>
            )}
        </Box>
    );

    return (
        <Box position="relative" h="100%" overflow="hidden">
            <Flex h="full">
                {/* Sidebar */}
                <Box
                    position={{ base: "fixed", md: "fixed" }}
                    top={{ base: "60px", md: "74px" }}
                    left={{ base: sidebarOpen ? 0 : "-100%", md: sidebarOpen ? 0 : -350 }}
                    width={{ base: "calc(100% - 64px)", md: "350px" }}
                    height={{ base: "calc(100dvh - 60px)", md: "calc(100dvh - 74px)" }}
                    bg="white"
                    boxShadow="lg"
                    zIndex={15}
                    transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                    overflow="hidden"
                >
                    {sidebarOpen && (
                        <FilterWaterLevel
                            filters={filters}
                            onFilterChange={handleFilterChange}
                            onClearFilters={clearFilters}
                            onApplyFilters={handleApplyFilters}
                        />
                    )}
                </Box>

                {/* Toggle Sidebar Button */}
                <Box
                    position="fixed"
                    top={{ base: "48px", md: "34px" }}
                    left={{
                        base: sidebarOpen ? "calc(100% - 56px)" : "10px",
                        md: sidebarOpen ? "360px" : "10px",
                    }}
                    zIndex={200}
                    transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                >
                    {sidebarOpen ? (
                        <IconButton
                            aria-label="Sembunyikan Sidebar"
                            icon={<MdOutlineKeyboardArrowLeft />}
                            size="lg"
                            top={{ base: 5, md: 10 }}
                            onClick={() => setSidebarOpen(false)}
                            borderRadius="md"
                            boxShadow="md"
                            color="white"
                            _hover={{ bg: "customGreen.600" }}
                        />
                    ) : (
                        <IconButton
                            aria-label="Tampilkan Sidebar"
                            icon={<MdOutlineKeyboardArrowRight />}
                            size="lg"
                            top={{ base: "20px", md: 10 }}
                            onClick={() => setSidebarOpen(true)}
                            borderRadius="md"
                            boxShadow="md"
                            bg="customGreen.500"
                            color="white"
                            _hover={{ bg: "customGreen.600" }}
                        />
                    )}
                </Box>

                {/* Map Container */}
                <Box
                    flex={1}
                    position="relative"
                    ml={{ base: 0, md: sidebarOpen ? "350px" : 0 }}
                >
                    <Box
                        ref={mapContainerRef}
                        id="map"
                        h="full"
                        w="full"
                        sx={{
                            ".maplibre-gl-canvas": {
                                transition: "filter 0.3s",
                            },
                            "&:hover .maplibre-gl-canvas": {
                                filter: "brightness(0.98)",
                            },
                        }}
                    />

                    <Flex
                        position="absolute"
                        bottom={{ base: 4, sm: 6, md: 8 }}
                        right={{ base: 4, sm: 3, md: 8 }}
                        direction="column"
                        align="center"
                        gap={2}
                        zIndex={10}
                        sx={{ bottom: { base: "max(16px, env(safe-area-inset-bottom, 16px))", sm: "24px", md: "32px" } }}
                    >
                        <IconButton
                            aria-label="Show Legend"
                            icon={<MdOutlineLegendToggle />}
                            onClick={() => setIsLegendOpen(true)}
                            variant="solid"
                            colorScheme="green"
                            borderRadius="full"
                            boxShadow="lg"
                            w="40px"
                            h="40px"
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            mb={2}
                        />
                        <IconButton
                            aria-label="Toggle Satelit"
                            icon={<Text fontSize="x-small">{isSatellite ? "Satelit" : "Peta"}</Text>}
                            onClick={toggleSatellite}
                            variant="solid"
                            colorScheme="blue"
                            borderRadius="full"
                            boxShadow="lg"
                            w="40px"
                            h="40px"
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                        />
                        {filters.show_inspection_points && (
                            <IconButton
                                aria-label={showConnectionLines ? "Hide Connection Lines" : "Show Connection Lines"}
                                icon={showConnectionLines ? <MdClose /> : <TbLine />}
                                onClick={() => setShowConnectionLines(!showConnectionLines)}
                                variant="solid"
                                colorScheme="blue"
                                borderRadius="full"
                                boxShadow="lg"
                                w="40px"
                                h="40px"
                                display="flex"
                                flexDirection="column"
                                alignItems="center"
                                justifyContent="center"
                            />
                        )}
                        <IconButton
                            aria-label="Focus area"
                            icon={<MdMyLocation size={20} />}
                            onClick={focusToArea}
                            variant="solid"
                            colorScheme="blue"
                            borderRadius="full"
                            boxShadow="lg"
                            w="40px"
                            h="40px"
                        />
                        <ButtonGroup
                            variant="solid"
                            colorScheme="blue"
                            isAttached
                            borderRadius="full"
                            boxShadow="lg"
                            orientation="vertical"
                        >
                            <IconButton
                                aria-label="Zoom in"
                                icon={<FiPlus size={20} />}
                                onClick={zoomIn}
                                borderBottomRadius="0"
                                w="40px"
                                h="40px"
                            />
                            <IconButton
                                aria-label="Zoom out"
                                icon={<FiMinus size={20} />}
                                onClick={zoomOut}
                                borderTopRadius="0"
                                w="40px"
                                h="40px"
                            />
                        </ButtonGroup>
                    </Flex>
                </Box>
            </Flex>

            {/* Detail Modal */}
            <Modal
                isOpen={!!(selectedItem || selectedBlock)}
                onClose={() => {
                    setSelectedItem(null);
                    setSelectedBlock(null);
                }}
                size={{ base: "lg", md: "lg" }}
                isCentered={true}
                scrollBehavior="inside"
            >
                <ModalOverlay bg="blackAlpha.300" />
                <ModalContent
                    borderRadius="lg"
                    overflow="hidden"
                    id="modal-detail-water-level"
                >
                    <ModalHeader bg="customGold.500" borderBottomWidth="1px" borderColor="gray.100">
                        <HStack justify="space-between" align="center">
                            <VStack align="start" spacing={1}>
                                {selectedItem ? (
                                    <>
                                        <Text color="white" fontSize="x-large" fontWeight="bold">
                                            Detail {dashboardTitle}
                                        </Text>
                                        <Text color="whiteAlpha.900" fontSize="sm">
                                            {selectedItem.blok} • {selectedItem.estate}
                                        </Text>
                                    </>
                                ) : selectedBlock ? (
                                    <>
                                        <Text color="white" fontSize="x-large" fontWeight="bold">
                                            Detail {selectedBlock.waterLevelData && selectedBlock.waterLevelData.length > 0
                                                ? selectedBlock.waterLevelData[0].category
                                                : "Blok"}
                                        </Text>
                                        {/* Badge */}
                                        {(() => {
                                            let badge = null;
                                            const season = filters.season || 'normal';
                                            if (selectedItem) {
                                                badge = (
                                                    <Badge
                                                        bg={getColorByDashboardType(
                                                            filters.dashboard_type || 'water_level',
                                                            (selectedItem as WaterLevelData)?.water_level_qty ?? 0,
                                                            season
                                                        )}
                                                        variant="solid"
                                                        fontSize="xs"      // lebih kecil
                                                        px={1}             // padding horizontal lebih kecil
                                                        py={0.5}           // padding vertical lebih kecil
                                                        borderRadius="md"
                                                    >
                                                        {getLabelByDashboardType(
                                                            filters.dashboard_type || 'water_level',
                                                            (selectedItem as WaterLevelData)?.water_level_qty ?? 0,
                                                            season
                                                        )}
                                                    </Badge>
                                                );
                                            } else if (selectedBlock?.waterLevelData?.length) {
                                                const latestInspection = selectedBlock.waterLevelData[0];
                                                const badgeColor = getColorByDashboardType(
                                                    filters.dashboard_type || 'water_level',
                                                    latestInspection?.water_level_qty ?? 0,
                                                    season
                                                );
                                                const badgeLabel = getLabelByDashboardType(
                                                    filters.dashboard_type || 'water_level',
                                                    latestInspection?.water_level_qty ?? 0,
                                                    season
                                                );
                                                const isYellowBg = badgeColor.toLowerCase() === '#f1f429ff';
                                                badge = (
                                                    <Badge
                                                        bg={badgeColor}
                                                        color={isYellowBg ? "black" : "white"}
                                                        variant="solid"
                                                        fontSize="xs"
                                                        px={1}
                                                        py={0.5}
                                                        borderRadius="md"
                                                    >
                                                        {badgeLabel}
                                                    </Badge>
                                                );
                                            }
                                            return badge;
                                        })()}
                                    </>
                                ) : null}
                            </VStack>
                        </HStack>
                    </ModalHeader>
                    <ModalCloseButton color="white" />
                    <ModalBody py={4}>
                        {/* Block Detail */}
                        {selectedBlock && !selectedItem && (
                            <VStack spacing={4} align="stretch">
                                {/* Basic Information - menggunakan Grid seperti MapInspection */}
                                <Grid templateColumns="repeat(2, 1fr)" gap={2}>
                                    <DetailItem label="Estate" value={
                                        selectedBlock.waterLevelData && selectedBlock.waterLevelData.length > 0
                                            ? selectedBlock.waterLevelData[0].estate
                                            : "Tidak tersedia"
                                    } />
                                    <DetailItem label="Divisi" value={
                                        selectedBlock.waterLevelData && selectedBlock.waterLevelData.length > 0
                                            ? selectedBlock.waterLevelData[0].divisi
                                            : "Tidak tersedia"
                                    } />
                                    <DetailItem label="Blok" value={selectedBlock.isLKK ? selectedBlock.blok_code : selectedBlock.LandUse_26} />

                                    {selectedBlock.waterLevelData && selectedBlock.waterLevelData.length > 0 && (
                                        <>
                                            <DetailItem
                                                label={selectedBlock.waterLevelData[0].job_position || "Jabatan"}
                                                value={`${selectedBlock.waterLevelData[0].user} - ${selectedBlock.waterLevelData[0].nomor_induk_pegawai}`}
                                            />
                                            <DetailItem
                                                label="Tanggal"
                                                value={`${formatDateTime(`${selectedBlock.waterLevelData[0].tanggal} ${selectedBlock.waterLevelData[0].waktu}`).date} ${formatDateTime(`${selectedBlock.waterLevelData[0].tanggal} ${selectedBlock.waterLevelData[0].waktu}`).time}`}
                                            />
                                            <DetailItem
                                                label="Koordinat"
                                                value={`${selectedBlock.waterLevelData[0].latitude.toFixed(6)}, ${selectedBlock.waterLevelData[0].longitude.toFixed(6)}`}
                                            />
                                        </>
                                    )}
                                </Grid>

                                {/* Hasil Laporan */}
                                {selectedBlock.waterLevelData && selectedBlock.waterLevelData.length > 0 ? (
                                    (() => {
                                        const latestInspection = selectedBlock.waterLevelData[0];
                                        return (
                                            <Box>
                                                <Grid templateColumns="repeat(2, 1fr)" gap={4} mb={4}>
                                                    <DetailItem
                                                        label={latestInspection.category}
                                                        value={`${latestInspection.water_level_qty} ${latestInspection.category === 'Curah Hujan' ? 'mm' : 'cm'}`}
                                                    />
                                                    <DetailItem
                                                        label="WMS Status"
                                                        value={(() => {
                                                            let wmsStatusLabel = latestInspection.wms_status;
                                                            if (latestInspection.wms_status === 'b') {
                                                                wmsStatusLabel = 'Bersih';
                                                            } else if (latestInspection.wms_status === 'k') {
                                                                wmsStatusLabel = 'Kotor';
                                                            } else if (latestInspection.wms_status === 'r') {
                                                                wmsStatusLabel = 'Rusak';
                                                            }
                                                            return wmsStatusLabel;
                                                        })()}
                                                    />
                                                </Grid>

                                                {/* Keterangan Inspeksi */}
                                                {latestInspection.hasil_inspeksi && (
                                                    <Box mt={4}>
                                                        <Text fontWeight="500" mb={0}>
                                                            Hasil Inspeksi
                                                        </Text>
                                                        <Text fontSize="sm" color="gray.600" whiteSpace="pre-line">
                                                            {latestInspection.hasil_inspeksi}
                                                        </Text>
                                                    </Box>
                                                )}

                                                {/* Foto */}
                                                {latestInspection.pictures && latestInspection.pictures.length > 0 && (
                                                    <Box w="full">
                                                        <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={2}>
                                                            Foto:
                                                        </Text>
                                                        <Grid
                                                            templateColumns={{
                                                                base: "repeat(3, 1fr)",
                                                                sm: "repeat(4, 1fr)",
                                                                md: "repeat(5, 1fr)"
                                                            }}
                                                            gap={2}
                                                        >
                                                            {latestInspection.pictures.map((picture, index) => (
                                                                <Box key={picture.id} position="relative">
                                                                    <Image
                                                                        src={`${API_BASE_URL}${picture.server_uri}`}
                                                                        alt={`Foto ${index + 1}`}
                                                                        w="full"
                                                                        h="100%"
                                                                        objectFit="cover"
                                                                        borderRadius="md"
                                                                        border="2px solid"
                                                                        borderColor="gray.200"
                                                                        cursor="pointer"
                                                                        transition="all 0.2s"
                                                                        _hover={{
                                                                            transform: "scale(1.05)",
                                                                            borderColor: "yellow.400",
                                                                            shadow: "md"
                                                                        }}
                                                                        onClick={() => setSelectedImage(`${API_BASE_URL}${picture.server_uri}`)}
                                                                        fallback={
                                                                            <Center
                                                                                h="60px"
                                                                                bg="gray.100"
                                                                                borderRadius="md"
                                                                                border="2px solid"
                                                                                borderColor="gray.200"
                                                                            >
                                                                                <Spinner size="sm" color="yellow.500" />
                                                                            </Center>
                                                                        }
                                                                    />
                                                                    {/* <Badge
                                                                        position="absolute"
                                                                        top={1}
                                                                        right={1}
                                                                        colorScheme="yellow"
                                                                        variant="solid"
                                                                        fontSize="xs"
                                                                        borderRadius="full"
                                                                        minW="16px"
                                                                        h="16px"
                                                                        display="flex"
                                                                        alignItems="center"
                                                                        justifyContent="center"
                                                                    >
                                                                        {index + 1}
                                                                    </Badge> */}
                                                                </Box>
                                                            ))}
                                                        </Grid>
                                                    </Box>
                                                )}
                                            </Box>
                                        );
                                    })()
                                ) : (
                                    <Center
                                        py={12}
                                        bg="gray.50"
                                        borderRadius="lg"
                                        border="2px dashed"
                                        borderColor="gray.300"
                                    >
                                        <VStack spacing={3}>
                                            <Text fontSize="3xl">📊</Text>
                                            <Text color="gray.500" textAlign="center" fontSize="md">
                                                Tidak ada data inspeksi {dashboardTitle.toLowerCase()} untuk blok ini
                                            </Text>
                                        </VStack>
                                    </Center>
                                )}
                            </VStack>
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>

            {/* Legend Modal */}
            <Modal
                isOpen={isLegendOpen}
                onClose={() => setIsLegendOpen(false)}
                size="md"
                isCentered
            >
                <ModalOverlay bg="blackAlpha.300" />
                <ModalContent borderRadius="2xl" overflow="hidden">
                    <ModalHeader bg="customGold.500" color="white">
                        Legend {dashboardTitle}
                    </ModalHeader>
                    <ModalCloseButton color="white" />
                    <ModalBody p={6}>
                        <VStack spacing={3} align="stretch">
                            <Text fontWeight="bold" mb={2} textTransform="capitalize">
                                Skala WMS {filters.dashboard_type === 'curah_hujan' ? 'Curah Hujan' : filters.season || 'normal'}:
                            </Text>
                            {(filters.dashboard_type === 'curah_hujan' 
                                ? RAINFALL_COLORS 
                                : filters.season === 'kemarau' 
                                    ? WATER_LEVEL_KEMARAU_COLORS 
                                    : WATER_LEVEL_COLORS
                            ).map((config) => (
                                <LegendItem
                                    key={`${config.min}-${config.max}`}
                                    color={config.color}
                                    description={config.label}
                                />
                            ))}
                        </VStack>
                    </ModalBody>
                </ModalContent>
            </Modal>

            {/* Image Preview Modal */}
            <Modal
                isOpen={!!selectedImage}
                onClose={() => setSelectedImage(null)}
                size="xl"
                isCentered
            >
                <ModalOverlay bg="blackAlpha.800" />
                <ModalContent bg="transparent" boxShadow="none">
                    {/* <ModalCloseButton 
                        color="white" 
                        size="lg"
                        top={4}
                        right={4}
                        bg="blackAlpha.600"
                        _hover={{ bg: "blackAlpha.800" }}
                        borderRadius="full"
                    /> */}
                    <ModalBody display="flex" alignItems="center" justifyContent="center" p={8}>
                        {selectedImage && (
                            <Image
                                src={selectedImage}
                                alt="Preview"
                                maxW="90vw"
                                maxH="90vh"
                                objectFit="contain"
                                borderRadius="lg"
                                boxShadow="2xl"
                            />
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default MapWaterLevelComponent;
